import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { FamilyRaceLivePreview } from "./FamilyRaceLivePreview";
import "./FamilyRaceLivePreview.css";

/**
 * Feature flag — flip to `false` to instantly restore the previous
 * static-only Family Race banner. When disabled, no interval runs,
 * no preview mounts, no extra listeners are attached.
 */
export const ENABLE_FAMILY_RACE_LIVE_PREVIEW = true;

// Alternator timing (ms). Keep both windows generous enough that the
// viewer registers each state before the transition.
const BANNER_MS = 4800;
const PREVIEW_MS = 4800;
const SCAN_MS = 520;

/**
 * ProphetsGames — Section 4 (`#games`), data-driven.
 *
 * Family Race relocated to the Hub (`/hub/family-race`) — the public
 * landing GAMES section no longer surfaces it. Adding a fourth game
 * later only requires appending one entry to GAMES.
 * Card variant is derived from `status`:
 *   - "ended" → completed (external link, muted silver, no live pulse)
 *   - "live"  → active   (internal Link, dominant, LIVE pulsing dot)
 *   - "soon"  → upcoming (non-interactive, gold COMING SOON badge)
 */
const GAMES = [
  {
    id: "wcn-2026",
    title: "WCN 2026",
    status: "ended",
    description: "A World Cup NFT experience where collectors supported their nations and competed through the tournament.",
    image: "/img/games/wcn-2026.png",
    href: "https://wcn26.fun/",
    external: true,
    cta: "VIEW ARCHIVE",
    alt: "WCN 2026 — completed World Cup NFT project",
  },
];

const STATUS_LABEL = { ended: "ENDED", live: "LIVE", soon: "COMING SOON" };
const GAME_INDEX = { "wcn-2026": "01" };

function GameCardInner({ g }) {
  // Only the Family Race card ("live") gets the alternating preview.
  // Every other card keeps its exact current media element untouched.
  const enablePreview =
    ENABLE_FAMILY_RACE_LIVE_PREVIEW && g.id === "family-race";

  return (
    <>
      <span aria-hidden="true" className="prophets-game-corner prophets-game-corner--tl" />
      <span aria-hidden="true" className="prophets-game-corner prophets-game-corner--tr" />
      <span aria-hidden="true" className="prophets-game-corner prophets-game-corner--bl" />
      <span aria-hidden="true" className="prophets-game-corner prophets-game-corner--br" />

      {enablePreview ? (
        <FamilyRaceMediaAlternator g={g} />
      ) : (
        <div className="prophets-game-media">
          <img
            src={g.image}
            alt={g.alt}
            className="prophets-game-image"
            loading="lazy"
            draggable={false}
          />
          <span aria-hidden="true" className="prophets-game-scrim" />
        </div>
      )}

      <div className="prophets-game-body">
        <div className="prophets-game-body-head">
          <span
            className={`prophets-game-status prophets-game-status--${g.status}`}
            role="status"
          >
            {g.status === "live" && <span className="prophets-game-status-dot" aria-hidden="true" />}
            {g.status === "soon" && <span className="prophets-game-status-tick" aria-hidden="true" />}
            {g.status === "ended" && <span className="prophets-game-status-tick" aria-hidden="true" />}
            <span className="p-mono">{STATUS_LABEL[g.status]}</span>
          </span>
          <span className="p-mono prophets-game-index">GAME · {GAME_INDEX[g.id]}</span>
        </div>

        <h3
          className="p-nav prophets-game-title"
          data-testid={`game-${g.id}-title`}
        >
          {g.title}
        </h3>
        <p className="prophets-game-desc">{g.description}</p>

        {g.cta && (
          <span data-testid={`game-${g.id}-cta`} className="prophets-game-cta">
            <span className="p-mono">{g.cta}</span>
            {g.external ? (
              <ArrowUpRight className="w-4 h-4" strokeWidth={1.5} />
            ) : (
              <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            )}
          </span>
        )}
      </div>
    </>
  );
}

/**
 * FamilyRaceMediaAlternator
 * ---------------------------------------------------------------
 * Fixed-size container that occupies the exact same rectangle as
 * the current static banner. Cross-fades between two absolutely-
 * positioned layers (STATIC → PREVIEW → STATIC → …) with a short
 * sci-fi scan overlay during the switch. The layout NEVER changes,
 * so the card cannot resize or shift the content below.
 *
 * Isolation:
 *   • the preview reads only local state (no fetch, no WS, no DB)
 *   • the interval pauses when the section is off-screen
 *   • prefers-reduced-motion → freeze on the static banner
 *   • on very small screens the switch continues but the mini board
 *     compresses gracefully (see FamilyRaceLivePreview.css)
 * ---------------------------------------------------------------
 */
function FamilyRaceMediaAlternator({ g }) {
  const wrapRef = useRef(null);
  const [mode, setMode] = useState("static"); // "static" | "preview"
  const modeRef = useRef("static");
  const [scanning, setScanning] = useState(false);
  const [visible, setVisible] = useState(true);
  const [reducedMotion] = useState(() =>
    typeof window !== "undefined"
    && !!window.matchMedia
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  // Pause when off-screen. Same threshold as the section-level observer.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (reducedMotion || !visible) return undefined;

    let cancelled = false;
    const timers = [];
    const nextCycle = () => {
      // Trigger scan overlay, then flip mode after the scan reaches mid-sweep.
      setScanning(true);
      timers.push(window.setTimeout(() => {
        if (cancelled) return;
        const next = modeRef.current === "static" ? "preview" : "static";
        modeRef.current = next;
        setMode(next);
      }, Math.floor(SCAN_MS / 2)));
      timers.push(window.setTimeout(() => {
        if (cancelled) return;
        setScanning(false);
        // Schedule the next flip based on the newly-entered state.
        const delay = modeRef.current === "static" ? BANNER_MS : PREVIEW_MS;
        timers.push(window.setTimeout(nextCycle, delay));
      }, SCAN_MS));
    };

    // First flip after the initial static window elapses.
    timers.push(window.setTimeout(nextCycle, BANNER_MS));
    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
    };
  }, [visible, reducedMotion]);

  const showStatic = mode === "static";
  const previewActive = mode === "preview";

  return (
    <div
      ref={wrapRef}
      className="prophets-game-media prophets-game-media--switch"
      data-testid="family-race-media-alternator"
      data-mode={mode}
    >
      {/* Static banner layer */}
      <div className={`fr-media-layer ${showStatic ? "is-showing" : ""} ${scanning ? "is-transitioning" : ""}`}>
        <img
          src={g.image}
          alt={g.alt}
          className="prophets-game-image"
          loading="lazy"
          draggable={false}
        />
        <span aria-hidden="true" className="prophets-game-scrim" />
      </div>

      {/* Live-preview layer — only ticks while active AND in view */}
      <div className={`fr-media-layer ${previewActive ? "is-showing" : ""} ${scanning ? "is-transitioning" : ""}`}>
        <FamilyRaceLivePreview active={previewActive && visible && !reducedMotion} />
        <span aria-hidden="true" className="prophets-game-scrim" />
      </div>

      {/* Sci-fi scan overlay — only visible for the brief transition */}
      <span aria-hidden="true" className={`fr-media-scan ${scanning ? "is-active" : ""}`} key={scanning ? "on" : "off"} />
    </div>
  );
}


function GameCard({ g }) {
  const testId = `game-${g.id}`;
  const cls = `prophets-game-card prophets-game-card--${g.status}`;

  if (g.status === "live") {
    return (
      <Link
        to={g.href}
        data-testid={testId}
        aria-label={`Enter ${g.title}`}
        className={cls}
      >
        <GameCardInner g={g} />
      </Link>
    );
  }
  if (g.status === "ended") {
    return (
      <a
        href={g.href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        aria-label={`${g.title} — view archived project (opens in new tab)`}
        className={cls}
      >
        <GameCardInner g={g} />
      </a>
    );
  }
  // soon — not interactive
  return (
    <article data-testid={testId} aria-label={g.alt} className={cls}>
      <GameCardInner g={g} />
    </article>
  );
}

export function ProphetsGames() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") { setInView(true); return undefined; }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); io.disconnect(); }
    }, { threshold: 0.12, rootMargin: "0px 0px -8% 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      id="games"
      ref={ref}
      data-testid="games-section"
      className={`prophets-games ${inView ? "is-in-view" : ""} relative w-full px-5 md:px-10 py-10 md:py-12`}
    >
      <header className="relative z-10 mx-auto max-w-[1360px] w-full mb-5 md:mb-7 text-center">
        <h2 data-testid="games-title" className="p-nav prophets-games-title">
          Games
        </h2>
        <p className="p-mono prophets-games-lede">
          Prediction. Community. Strategy.
        </p>
      </header>

      {/* Public GAMES section — Family Race relocated into the Hub.
          Only the WCN archive card remains here. Layout is a single
          centred card at up to a compact width, keeping the visual
          balance of the section without empty space. */}
      <div className="relative z-10 mx-auto max-w-[720px] w-full grid grid-cols-1 gap-5 md:gap-6 items-stretch">
        <div
          data-testid="games-desktop-only-card"
          className="prophets-games-wcn-wrap"
        >
          <GameCard g={GAMES[0]} />
        </div>
      </div>
    </section>
  );
}
